import EventEmitter from "../EventEmitter";
import { resolveContainer } from "../component/domUtils";
import IntegrationError from "../IntegrationError";
import Controller from "../controller/Controller";
import CheckoutFrame from "./CheckoutFrame";
import { COMPANY_NAME } from "../../env";
import { SuccessPayload } from "../types/checkout/SuccessPayload";

const VOID_ELEMENTS = ['AREA', 'BASE', 'BR', 'COL', 'COMMAND', 'EMBED', 'HR', 'IMG', 'INPUT', 'KEYGEN', 'LINK', 'META', 'PARAM', 'SOURCE', 'TRACK', 'WBR'];

const STYLES = {
    margin: 0,
    padding: 0,
    border: "none",
    display: "block",
    background: "transparent",
    position: "relative",
    opacity: 1,
    height: '100%',
    width: '100%',
};

export default class Checkout extends EventEmitter {
    _frame: CheckoutFrame;
    _component: HTMLDivElement;
    _container: HTMLElement;
    private _onSuccess: ((payload: SuccessPayload) => void) | undefined;
    private _onFailure: ((error: string) => void) | undefined;
    private _destroyed = false;

    constructor(controller: Controller, options: any = {}) {
        super();

        const { onSuccess, onFailure, ...frameOptions } = options;
        this._onSuccess = onSuccess;
        this._onFailure = onFailure;

        const component = document.createElement("div");
        component.className = `__private${COMPANY_NAME}Component`;
        component.style.cssText = Object.entries(STYLES)
            .map(([style, value]) => `${style}: ${value} !important;`)
            .join(" ");

        const frame = new CheckoutFrame(controller._id, frameOptions);
        controller.addFrame(frame);

        this._component = component;
        this._frame = frame;

        frame.on('load', () => this.emit("ready"));
        frame.on('container-height', (data) => {
            this._component.style.setProperty('height', data.height, 'important');
        });
        frame.on('success', (payload) => this._onSuccess?.(payload));
        frame.on('failure', ({ error }) => this._onFailure?.(error));
    }

    mount(selector: string | HTMLElement): this {
        const container = resolveContainer(selector);

        if (this._destroyed) {
            throw new IntegrationError("Cannot mount a destroyed checkout. Create a new instance with a new clientSecret.");
        }
        if (VOID_ELEMENTS.indexOf(container.nodeName) !== -1) {
            throw new IntegrationError("Container should be a DOM element which can have children.");
        }
        if (this._frame._isMounted()) {
            throw new IntegrationError("Component is already mounted.");
        }
        if (container.children.length) {
            console.warn("Component will be mounted to DOM element with children.");
        }

        this._frame.appendTo(this._component);
        container.appendChild(this._component);
        this._container = container;

        return this;
    }

    destroy(): void {
        this._destroyed = true;
        this._frame.destroy();
        this._component.remove();
    }
}
